"use client";

import { Container, Title, Paper, Group, Button, Stack, Text, Box, SimpleGrid, Badge, Card, Divider } from "@mantine/core";
import { NavigationProgress, setNavigationProgress } from "@mantine/nprogress";
import { IconArrowRight, IconRefresh } from "@tabler/icons-react";
import { useState, useEffect } from "react";
import { Link } from "@/i18n/navigation";
import { trackQuizStart, trackQuizComplete, trackCtaClick } from "@/lib/analytics";
import TypeformQuiz, { QuizQuestion } from "@/components/TypeformQuiz";
import QuizPhotoUpload from "@/components/QuizPhotoUpload";
import KibbeOffer from "@/components/kibbe/KibbeOffer";
import EmailResultsCapture from "@/components/EmailResultsCapture";

// Style Personality Types
type StylePersonality = "classic" | "romantic" | "dramatic" | "natural" | "creative" | "minimalist";

interface StyleResult {
  name: string;
  tagline: string;
  description: string;
  keyTraits: string[];
  wardrobeEssentials: string[];
  avoidItems: string[];
  colorPaletteAdvice: string;
  shoppingTip: string;
  celebrities: string[];
}

const styleResults: Record<StylePersonality, StyleResult> = {
  classic: {
    name: "Classic",
    tagline: "Timeless elegance, always appropriate",
    description:
      "You gravitate toward timeless, elegant pieces that never go out of style. Quality over quantity is your mantra. You prefer investment pieces that will last for years rather than trendy items. Your wardrobe is refined, polished, and versatile.",
    keyTraits: ["Timeless", "Polished", "Refined", "Elegant", "Quality-focused"],
    wardrobeEssentials: [
      "Tailored blazer in navy or black",
      "White button-down shirt",
      "Well-fitted dark jeans or trousers",
      "Cashmere sweater",
      "Trench coat",
      "Leather loafers or pumps",
      "Pearl or diamond studs",
      "Structured handbag",
    ],
    avoidItems: [
      "Overly trendy pieces",
      "Loud patterns",
      "Excessive embellishments",
      "Ill-fitting clothes",
      "Fast fashion items",
    ],
    colorPaletteAdvice:
      "Stick to neutrals and timeless colors: navy, black, white, cream, camel, burgundy, and forest green. These colors mix effortlessly and create a sophisticated wardrobe.",
    shoppingTip:
      "Invest in quality basics. A well-made blazer or cashmere sweater will serve you for decades and cost less per wear than cheaper alternatives.",
    celebrities: ["Audrey Hepburn", "Kate Middleton", "Meghan Markle", "Amal Clooney"],
  },
  romantic: {
    name: "Romantic",
    tagline: "Soft, feminine, and dreamy",
    description:
      "You're drawn to soft, feminine silhouettes and delicate details. Florals, lace, and flowing fabrics make your heart sing. Your style is graceful, gentle, and often inspired by vintage aesthetics. You love pieces that make you feel beautiful and dreamy.",
    keyTraits: ["Feminine", "Soft", "Graceful", "Delicate", "Dreamy"],
    wardrobeEssentials: [
      "Flowy midi dress with florals",
      "Silk or satin blouse",
      "Lace-trimmed camisole",
      "A-line or pleated skirt",
      "Soft cardigan",
      "Delicate jewelry with pearls or rose gold",
      "Ballet flats or kitten heels",
      "Romantic handbag with soft curves",
    ],
    avoidItems: [
      "Sharp, angular pieces",
      "Heavy leather",
      "Harsh colors",
      "Oversized or boxy silhouettes",
      "Industrial or utilitarian styles",
    ],
    colorPaletteAdvice:
      "Embrace soft, muted colors: blush pink, lavender, soft blue, cream, rose, mauve, and sage green. These colors complement your gentle aesthetic perfectly.",
    shoppingTip:
      "Look for pieces with beautiful details like embroidery, lace trim, or subtle prints. Vintage and vintage-inspired pieces often have the romantic details you love.",
    celebrities: ["Taylor Swift", "Keira Knightley", "Amanda Seyfried", "Dakota Johnson"],
  },
  dramatic: {
    name: "Dramatic",
    tagline: "Bold, striking, and unforgettable",
    description:
      "You love making a statement. Bold silhouettes, striking contrasts, and architectural pieces are your signature. You're not afraid to stand out and express your powerful presence through fashion. Your style commands attention.",
    keyTraits: ["Bold", "Striking", "Powerful", "Confident", "Avant-garde"],
    wardrobeEssentials: [
      "Structured blazer with sharp shoulders",
      "Statement coat in bold color or texture",
      "High-contrast pieces (black & white)",
      "Sleek leather pants or skirt",
      "Dramatic maxi dress",
      "Bold statement jewelry",
      "Pointed-toe heels or architectural boots",
      "Structured, geometric handbag",
    ],
    avoidItems: [
      "Overly sweet or cutesy items",
      "Soft, frilly details",
      "Busy small prints",
      "Anything that blends in",
      "Shapeless silhouettes",
    ],
    colorPaletteAdvice:
      "Embrace bold, striking colors: black, white, red, emerald, royal purple, and metallics. High contrast combinations create the drama you crave.",
    shoppingTip:
      "Look for pieces with interesting construction, unique silhouettes, or statement-making details. One dramatic piece can elevate an entire outfit.",
    celebrities: ["Cate Blanchett", "Angelina Jolie", "Tilda Swinton", "Lady Gaga"],
  },
  natural: {
    name: "Natural",
    tagline: "Relaxed, effortless, and authentic",
    description:
      "You value comfort and authenticity in your wardrobe. Relaxed silhouettes, natural fabrics, and earthy tones define your style. You look effortlessly put-together without appearing overly styled. Function and comfort are just as important as aesthetics.",
    keyTraits: ["Relaxed", "Authentic", "Effortless", "Comfortable", "Down-to-earth"],
    wardrobeEssentials: [
      "Perfectly worn-in jeans",
      "Soft cotton or linen button-down",
      "Comfortable knit sweater",
      "Utility jacket or denim jacket",
      "Relaxed blazer in natural fabric",
      "Quality leather belt",
      "Comfortable flat sandals or ankle boots",
      "Canvas or leather tote bag",
    ],
    avoidItems: [
      "Overly structured pieces",
      "Stiff fabrics",
      "Fussy details",
      "Uncomfortable shoes",
      "Anything that feels restrictive",
    ],
    colorPaletteAdvice:
      "Embrace earthy, natural tones: olive, tan, cream, rust, denim blue, and forest green. These colors reflect your connection to nature and authenticity.",
    shoppingTip:
      "Focus on quality natural fabrics like cotton, linen, wool, and leather. Well-made basics in comfortable fits will be your wardrobe heroes.",
    celebrities: ["Jennifer Aniston", "Gwyneth Paltrow", "Jessica Alba", "Gigi Hadid"],
  },
  creative: {
    name: "Creative",
    tagline: "Eclectic, artistic, and one-of-a-kind",
    description:
      "You see fashion as art and self-expression. Mixing patterns, playing with proportions, and combining unexpected pieces comes naturally to you. Your style is unique, imaginative, and often ahead of trends. You dress to express your creative spirit.",
    keyTraits: ["Artistic", "Eclectic", "Imaginative", "Unique", "Expressive"],
    wardrobeEssentials: [
      "Statement printed pieces",
      "Mix of textures and patterns",
      "Vintage or thrifted finds",
      "Artisanal or handmade jewelry",
      "Unique layering pieces",
      "Unexpected color combinations",
      "Statement shoes in unusual shapes",
      "One-of-a-kind accessories",
    ],
    avoidItems: [
      "Matching sets that feel too uniform",
      "Playing it safe",
      "Following trends blindly",
      "Cookie-cutter outfits",
      "Boring basics without personality",
    ],
    colorPaletteAdvice:
      "Embrace the unexpected! Mix colors that shouldn't work but do. Try complementary contrasts, analogous combinations, or monochromatic looks in bold hues.",
    shoppingTip:
      "Explore vintage shops, artisan markets, and independent designers. Your best finds often come from unexpected places. Don't be afraid to mix high and low.",
    celebrities: ["Sarah Jessica Parker", "Solange Knowles", "Björk", "Iris Apfel"],
  },
  minimalist: {
    name: "Minimalist",
    tagline: "Clean, intentional, and refined",
    description:
      "Less is more is your philosophy. You curate a capsule wardrobe of well-made essentials that work seamlessly together. Clean lines, quality fabrics, and intentional choices define your approach. Every piece in your wardrobe earns its place.",
    keyTraits: ["Intentional", "Clean", "Curated", "Refined", "Focused"],
    wardrobeEssentials: [
      "Perfectly fitted white t-shirt",
      "Black trousers with clean lines",
      "Cashmere sweater in neutral tone",
      "Minimalist blazer",
      "Simple slip dress",
      "Quality leather accessories",
      "Clean white sneakers",
      "Structured leather bag",
    ],
    avoidItems: [
      "Excessive patterns or prints",
      "Unnecessary embellishments",
      "Trendy pieces that don't last",
      "Cluttered accessories",
      "Anything that doesn't serve a purpose",
    ],
    colorPaletteAdvice:
      "Stick to a refined palette: black, white, gray, navy, camel, and one accent color you love. This creates endless mixing possibilities with minimal pieces.",
    shoppingTip:
      "Quality over quantity, always. Invest in pieces made from premium materials with excellent construction. A capsule wardrobe of 30-40 pieces is all you need.",
    celebrities: ["Carolyn Bessette-Kennedy", "Victoria Beckham", "Phoebe Philo", "Rosie Huntington-Whiteley"],
  },
};

// Map answer values to personality scores
const answerScores: Record<string, Partial<Record<StylePersonality, number>>> = {
  // Question 1: Dream Wardrobe
  "classic_minimalist": { classic: 3, minimalist: 2 },
  "romantic_classic": { romantic: 3, classic: 1 },
  "dramatic_creative": { dramatic: 3, creative: 1 },
  "natural_minimalist": { natural: 3, minimalist: 1 },
  "creative_dramatic": { creative: 3, dramatic: 1 },
  "minimalist_classic": { minimalist: 3, classic: 1 },
  // Question 2: Getting Dressed
  "classic_uniform": { classic: 3, minimalist: 2 },
  "romantic_beautiful": { romantic: 3 },
  "dramatic_statement": { dramatic: 3, creative: 1 },
  "natural_comfort": { natural: 3 },
  "creative_experiment": { creative: 3 },
  "minimalist_simple": { minimalist: 3 },
  // Question 3: Shopping
  "classic_invest": { classic: 3, minimalist: 2 },
  "romantic_details": { romantic: 3 },
  "dramatic_unique": { dramatic: 3, creative: 2 },
  "natural_comfort_shop": { natural: 3 },
  "creative_vintage": { creative: 3 },
  "minimalist_gap": { minimalist: 3 },
  // Question 4: Fabrics
  "classic_fabric": { classic: 3, minimalist: 1 },
  "romantic_fabric": { romantic: 3 },
  "dramatic_fabric": { dramatic: 3 },
  "natural_fabric": { natural: 3 },
  "creative_fabric": { creative: 3 },
  "minimalist_fabric": { minimalist: 3 },
  // Question 5: Accessories
  "classic_accessories": { classic: 3 },
  "romantic_accessories": { romantic: 3 },
  "dramatic_accessories": { dramatic: 3 },
  "natural_accessories": { natural: 3 },
  "creative_accessories": { creative: 3 },
  "minimalist_accessories": { minimalist: 3 },
  // Question 6: Style Icons
  "classic_icons": { classic: 3 },
  "romantic_icons": { romantic: 3 },
  "dramatic_icons": { dramatic: 3 },
  "natural_icons": { natural: 3 },
  "creative_icons": { creative: 3 },
  "minimalist_icons": { minimalist: 3 },
  // Question 7: Colors
  "classic_colors": { classic: 3, minimalist: 2 },
  "romantic_colors": { romantic: 3 },
  "dramatic_colors": { dramatic: 3 },
  "natural_colors": { natural: 3 },
  "creative_colors": { creative: 3 },
  "minimalist_colors": { minimalist: 3 },
  // Question 8: Events
  "classic_event": { classic: 3 },
  "romantic_event": { romantic: 3 },
  "dramatic_event": { dramatic: 3 },
  "natural_event": { natural: 3 },
  "creative_event": { creative: 3 },
  "minimalist_event": { minimalist: 3 },
};

// Simplified questions for Typeform-style experience
const questions: QuizQuestion[] = [
  {
    id: "wardrobe",
    title: "Your Dream Wardrobe",
    description: "If you could instantly replace your entire wardrobe, what would it look like?",
    options: [
      { label: "A curated collection of timeless investment pieces", value: "classic_minimalist" },
      { label: "Soft, feminine pieces with beautiful details", value: "romantic_classic" },
      { label: "Statement pieces that turn heads", value: "dramatic_creative" },
      { label: "Comfortable, quality basics in natural fabrics", value: "natural_minimalist" },
      { label: "A mix of unique finds and artistic pieces", value: "creative_dramatic" },
      { label: "A minimal capsule of perfectly curated essentials", value: "minimalist_classic" },
    ],
  },
  {
    id: "dressed",
    title: "Getting Dressed",
    description: "How do you typically feel about getting dressed each day?",
    options: [
      { label: "I love having a uniform of polished, reliable pieces", value: "classic_uniform" },
      { label: "I enjoy choosing pieces that make me feel beautiful", value: "romantic_beautiful" },
      { label: "I see it as an opportunity to make a statement", value: "dramatic_statement" },
      { label: "I prioritize comfort and grab what feels good", value: "natural_comfort" },
      { label: "I love experimenting and expressing my mood", value: "creative_experiment" },
      { label: "I keep it simple with my curated essentials", value: "minimalist_simple" },
    ],
  },
  {
    id: "shopping",
    title: "Shopping Approach",
    description: "When you shop for clothes, what's your typical approach?",
    options: [
      { label: "I invest in quality pieces that will last for years", value: "classic_invest" },
      { label: "I look for beautiful details and feminine touches", value: "romantic_details" },
      { label: "I hunt for bold, unique pieces that stand out", value: "dramatic_unique" },
      { label: "I focus on comfort, fit, and natural materials", value: "natural_comfort_shop" },
      { label: "I love vintage shops and one-of-a-kind items", value: "creative_vintage" },
      { label: "I only buy if it fills a specific gap in my wardrobe", value: "minimalist_gap" },
    ],
  },
  {
    id: "fabrics",
    title: "Favorite Fabrics",
    description: "Which fabrics make you feel best?",
    options: [
      { label: "Crisp cotton, fine wool, quality silk", value: "classic_fabric" },
      { label: "Soft silk, lace, flowing chiffon", value: "romantic_fabric" },
      { label: "Structured fabrics, leather, bold textures", value: "dramatic_fabric" },
      { label: "Linen, soft cotton, relaxed knits", value: "natural_fabric" },
      { label: "A mix of textures - velvet, brocade, unusual weaves", value: "creative_fabric" },
      { label: "Premium basics - Japanese cotton, cashmere", value: "minimalist_fabric" },
    ],
  },
  {
    id: "accessories",
    title: "Accessory Style",
    description: "How do you approach accessories?",
    options: [
      { label: "Classic pieces like pearls, a quality watch", value: "classic_accessories" },
      { label: "Delicate jewelry, florals, romantic details", value: "romantic_accessories" },
      { label: "Bold statement pieces that command attention", value: "dramatic_accessories" },
      { label: "Minimal, functional pieces in natural materials", value: "natural_accessories" },
      { label: "Eclectic mix of vintage finds and artisan pieces", value: "creative_accessories" },
      { label: "A few high-quality essentials that work with everything", value: "minimalist_accessories" },
    ],
  },
  {
    id: "icons",
    title: "Style Icons",
    description: "Whose style do you most admire?",
    options: [
      { label: "Audrey Hepburn, Kate Middleton, Amal Clooney", value: "classic_icons" },
      { label: "Taylor Swift, Keira Knightley, Florence Pugh", value: "romantic_icons" },
      { label: "Cate Blanchett, Angelina Jolie, Lady Gaga", value: "dramatic_icons" },
      { label: "Jennifer Aniston, Gwyneth Paltrow, Gigi Hadid", value: "natural_icons" },
      { label: "Sarah Jessica Parker, Solange, Iris Apfel", value: "creative_icons" },
      { label: "Victoria Beckham, Carolyn Bessette-Kennedy", value: "minimalist_icons" },
    ],
  },
  {
    id: "colors",
    title: "Color Preference",
    description: "Which color palette feels most like you?",
    options: [
      { label: "Navy, black, white, camel - timeless neutrals", value: "classic_colors" },
      { label: "Blush, lavender, soft pink - soft and romantic", value: "romantic_colors" },
      { label: "Bold red, black, white, metallics - high contrast", value: "dramatic_colors" },
      { label: "Olive, tan, rust, denim - earthy and natural", value: "natural_colors" },
      { label: "Unexpected color mixes - I love playing with combinations", value: "creative_colors" },
      { label: "Black, white, grey - clean and refined", value: "minimalist_colors" },
    ],
  },
  {
    id: "events",
    title: "Event Dressing",
    description: "You're going to a special event. What do you reach for?",
    options: [
      { label: "An elegant, timeless dress or well-tailored suit", value: "classic_event" },
      { label: "Something flowing and feminine with beautiful details", value: "romantic_event" },
      { label: "A statement piece that will turn heads", value: "dramatic_event" },
      { label: "Something comfortable but elevated", value: "natural_event" },
      { label: "Something unique that expresses my personality", value: "creative_event" },
      { label: "A refined, simple piece in a beautiful fabric", value: "minimalist_event" },
    ],
  },
];

function calculateStylePersonality(answers: Record<string, string>): {
  primary: StylePersonality;
  secondary: StylePersonality;
  scores: Record<StylePersonality, number>;
} {
  const scores: Record<StylePersonality, number> = {
    classic: 0,
    romantic: 0,
    dramatic: 0,
    natural: 0,
    creative: 0,
    minimalist: 0,
  };

  // Calculate scores from answers
  Object.values(answers).forEach((answerValue) => {
    const answerScore = answerScores[answerValue];
    if (answerScore) {
      Object.entries(answerScore).forEach(([personality, score]) => {
        scores[personality as StylePersonality] += score || 0;
      });
    }
  });

  // Sort to find primary and secondary
  const sortedScores = Object.entries(scores).sort((a, b) => b[1] - a[1]);

  return {
    primary: sortedScores[0][0] as StylePersonality,
    secondary: sortedScores[1][0] as StylePersonality,
    scores,
  };
}

export default function StylePersonalityQuiz() {
  const [phase, setPhase] = useState<"intro" | "quiz" | "results">("intro");
  const [answers, setAnswers] = useState<Record<string, string>>({});

  // Reset progress on mount
  useEffect(() => {
    setNavigationProgress(0);
  }, []);

  const startQuiz = () => {
    setPhase("quiz");
    trackQuizStart("Style Personality Quiz");
  };

  const handleQuizComplete = (quizAnswers: Record<string, string>) => {
    setAnswers(quizAnswers);
    setPhase("results");
    trackQuizComplete("Style Personality Quiz");
  };

  const handleExit = () => {
    setPhase("intro");
    setNavigationProgress(0);
  };

  const resetQuiz = () => {
    setPhase("intro");
    setAnswers({});
    setNavigationProgress(0);
  };

  if (phase === "results") {
    const { primary, secondary, scores } = calculateStylePersonality(answers);
    const result = styleResults[primary];
    const secondaryResult = styleResults[secondary];
    const primaryScore = scores[primary];
    const secondaryScore = scores[secondary];

    return (
      <Container size="xl" py="xl">
        <NavigationProgress size={4} color="violet" />
        <Title ta="center" mb="xl">
          Your Style Personality Results
        </Title>

        <Paper shadow="sm" p={{ base: "md", md: "xl" }} radius="md" mb="xl">
          <Stack gap="xl">
            {/* Primary Result */}
            <Box ta="center">
              <Badge size="lg" color="violet" mb="md">
                Your Style Personality
              </Badge>
              <Title order={2} mb="xs">
                {result.name}
              </Title>
              <Text c="dimmed" fz="lg" mb="md">
                "{result.tagline}"
              </Text>
              <Text>{result.description}</Text>

              {/* Secondary influence */}
              {secondaryScore > 0 && secondaryScore >= primaryScore * 0.6 && (
                <Paper withBorder p="md" radius="md" mt="md" bg="gray.0">
                  <Text fz="sm">
                    <strong>Secondary Influence:</strong> You also show strong {secondaryResult.name} tendencies, which adds{" "}
                    {secondary === "romantic"
                      ? "softness and femininity"
                      : secondary === "dramatic"
                        ? "boldness and edge"
                        : secondary === "natural"
                          ? "relaxed authenticity"
                          : secondary === "creative"
                            ? "artistic expression"
                            : secondary === "minimalist"
                              ? "refined simplicity"
                              : "timeless elegance"}{" "}
                    to your style.
                  </Text>
                </Paper>
              )}
            </Box>

            <QuizPhotoUpload quizName="Style Personality Quiz" />

            {/* Key Traits */}
            <Box>
              <Title order={4} mb="md">
                Your Key Style Traits
              </Title>
              <Group gap="xs">
                {result.keyTraits.map((trait, index) => (
                  <Badge key={index} variant="light" color="violet" size="lg">
                    {trait}
                  </Badge>
                ))}
              </Group>
            </Box>

            {/* Wardrobe Essentials */}
            <Box>
              <Title order={4} mb="md">
                Wardrobe Essentials for You
              </Title>
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xs">
                {result.wardrobeEssentials.map((item, index) => (
                  <Text key={index} fz="sm">
                    • {item}
                  </Text>
                ))}
              </SimpleGrid>
            </Box>

            {/* What to Avoid */}
            <Box>
              <Title order={4} mb="md" c="red.6">
                What to Avoid
              </Title>
              <Stack gap="xs">
                {result.avoidItems.map((item, index) => (
                  <Text key={index} fz="sm" c="dimmed">
                    • {item}
                  </Text>
                ))}
              </Stack>
            </Box>

            {/* Color Advice */}
            <Paper withBorder p="md" radius="md" bg="violet.0">
              <Title order={5} mb="xs">
                Color Palette Advice
              </Title>
              <Text fz="sm">{result.colorPaletteAdvice}</Text>
            </Paper>

            {/* Shopping Tip */}
            <Box>
              <Title order={5} mb="xs">
                Shopping Tip
              </Title>
              <Text fz="sm" c="dimmed">
                {result.shoppingTip}
              </Text>
            </Box>

            {/* Celebrity Style Matches */}
            <Box>
              <Title order={5} mb="xs">
                Celebrity Style Matches
              </Title>
              <Text fz="sm">{result.celebrities.join(", ")}</Text>
            </Box>

            <Button variant="subtle" onClick={resetQuiz} leftSection={<IconRefresh size="1rem" />} color="gray">
              Take the Quiz Again
            </Button>

            <Divider my="md" />
            <Box w="100%">
              <EmailResultsCapture
                source="style-personality-quiz"
                resultLabel={result.name}
                title={`Email me my ${result.name} style guide`}
                subtitle="Your style personality breakdown plus the colors that flatter it — free. Unsubscribe anytime."
              />
            </Box>

            <Paper withBorder p="md" radius="md" bg="violet.0">
              <Title order={5} mb="xs">
                Great style starts with your colors
              </Title>
              <Text fz="sm" mb="md">
                Your style personality tells you what to wear — your color season tells you which shades to wear it in.
              </Text>
              <Button
                component={Link}
                href="/ai-color-analysis?src=bridge_stylepq"
                color="violet"
                onClick={() => trackCtaClick("bridge_stylepq_ca")}
              >
                Find my color season
              </Button>
            </Paper>
          </Stack>
        </Paper>
        <KibbeOffer source="style_quiz_result" placement="free_result" showSample={false} />
      </Container>
    );
  }

  if (phase === "quiz") {
    return (
      <TypeformQuiz
        questions={questions}
        onComplete={handleQuizComplete}
        onExit={handleExit}
        accentColor="#7C3AED" // violet
      />
    );
  }

  // Intro phase
  return (
    <Container size="xl" py="xl">
      <NavigationProgress size={4} color="violet" />
      <Title ta="center" mb="xl">
        Style Personality Quiz
      </Title>

      <Stack gap="xl">
        <Box>
          <Group justify="space-between" align="center" mb="md">
            <Text size="lg">Discover your unique fashion archetype and get personalized style recommendations.</Text>
            <Button
              size="lg"
              onClick={startQuiz}
              rightSection={<IconArrowRight size="1.2rem" />}
              color="violet"
              variant="filled"
            >
              Start Quiz
            </Button>
          </Group>
          <Text mb="md">
            Your style personality is the foundation of your wardrobe. Understanding your natural style archetype
            helps you:
          </Text>
          <Stack gap="xs" ml="md">
            <Text>• Make confident wardrobe decisions that feel authentically you</Text>
            <Text>• Build a cohesive closet where everything works together</Text>
            <Text>• Stop wasting money on pieces that don't fit your style</Text>
            <Text>• Develop your signature look that reflects your personality</Text>
          </Stack>
        </Box>

        <Box>
          <Title order={3} mb="md">
            The 6 Style Personalities
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            {Object.entries(styleResults).map(([key, style]) => (
              <Paper key={key} withBorder p="md" radius="md">
                <Title order={5} mb="xs">
                  {style.name}
                </Title>
                <Text fz="sm" c="dimmed">
                  {style.tagline}
                </Text>
              </Paper>
            ))}
          </SimpleGrid>
        </Box>

        <Box>
          <Title order={3} mb="md">
            What You'll Discover
          </Title>
          <Stack gap="xs" ml="md">
            <Text>• Your primary style personality and what it means</Text>
            <Text>• Wardrobe essentials that suit your style</Text>
            <Text>• What to avoid in your closet</Text>
            <Text>• Color palette recommendations</Text>
            <Text>• Celebrity style matches for inspiration</Text>
          </Stack>
        </Box>

        <Button size="lg" onClick={startQuiz} rightSection={<IconArrowRight size="1.2rem" />} color="violet">
          Discover Your Style Personality
        </Button>

        <Box>
          <Title order={3} mb="md">
            Related Color & Style Tools
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Paper withBorder p="md" radius="md" component={Link} href="/color-analysis-quiz" style={{ textDecoration: "none" }}>
              <Title order={5} mb="xs">
                Color Analysis Quiz
              </Title>
              <Text fz="sm" c="dimmed">
                Find your color season and discover which colors make you look radiant.
              </Text>
            </Paper>
            <Paper withBorder p="md" radius="md" component={Link} href="/skin-tone-test" style={{ textDecoration: "none" }}>
              <Title order={5} mb="xs">
                Skin Tone Test
              </Title>
              <Text fz="sm" c="dimmed">
                Learn about your undertone with DIY tests you can do at home.
              </Text>
            </Paper>
            <Paper withBorder p="md" radius="md" component={Link} href="/kibbe-body-type-test" style={{ textDecoration: "none" }}>
              <Title order={5} mb="xs">
                Kibbe Body Type Test
              </Title>
              <Text fz="sm" c="dimmed">
                Discover your Kibbe body type for clothing silhouettes that flatter your frame.
              </Text>
            </Paper>
            <Paper withBorder p="md" radius="md" component={Link} href="/gold-or-silver-jewelry-test" style={{ textDecoration: "none" }}>
              <Title order={5} mb="xs">
                Gold or Silver Test
              </Title>
              <Text fz="sm" c="dimmed">
                Find out whether gold or silver jewelry complements your undertone.
              </Text>
            </Paper>
          </SimpleGrid>
        </Box>
      </Stack>
    </Container>
  );
}
